import React, { useEffect } from 'react'
import {
  Box,
  Grid,
  generateUtilityClasses,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TableSortLabel,
  Tooltip,
} from '@mui/material'
import { CircularProgress } from '@mui/material'
import { UspDealCommissionViewResult } from '../../../../../models'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { styled } from '@mui/system'
import { useNavigate, useParams } from 'react-router-dom'
import { appRoute } from '../../../../../constants'
import { storageGet, storageSet } from '../../../../../utils/storage'
import { stableSort, getComparator } from '../../../../../utils/sorting'
import { visuallyHidden } from '@mui/utils'
import ConfirmDialog from '../../../../../components/ConfirmDialog'
import { enqueueNotification } from '../../../../../redux/slices/notificationsSlice'
import { useDispatch } from 'react-redux'
import { useDeleteDealReferralMutation, useLazyGetDealReferralsQuery } from '../../../../../services'

const classes = generateUtilityClasses('Grid', [
  'root',
  'activeItem',
  'inactiveItem',
  'loading',
  'spinner',
  'tableHead',
  'tableCellName',
  'telegramIconActive',
  'telegramIconInactive',
])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
  [`& .${classes.telegramIconActive}`]: {
    color: theme.palette.success.main,
    '&:disabled': {
      color: theme.palette.text.disabled,
    },
  },
}))

interface Props {}

const DealsReferralTableBlock = ({}: Props) => {
  const [getDealReferrals, { data: referralData, isLoading }] = useLazyGetDealReferralsQuery()
  const [deleteConfirmDialog, setDeleteConfirmDialog] = React.useState<{ referralId: string } | undefined>(undefined)
  const [deleteReferral, { isSuccess: isSuccessDeleteReferral }] = useDeleteDealReferralMutation()
  const initialOrder = (storageGet('deals_referrals_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('deals_referrals_by') || 'Salesperson') as keyof UspDealCommissionViewResult
  const [order, setOrder] = React.useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = React.useState<keyof UspDealCommissionViewResult>(initialOrderBy)
  const { dealID } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspDealCommissionViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('deals_referrals_order', isAsc ? 'desc' : 'asc')
    storageSet('deals_referrals_by', property)
  }

  const createSortHandler = (property: keyof UspDealCommissionViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = () => {
    if (deleteConfirmDialog?.referralId) {
      deleteReferral({ dealId: dealID as string, referralId: deleteConfirmDialog?.referralId as string })
      setDeleteConfirmDialog(undefined)
    }
  }

  useEffect(() => {
    if (isSuccessDeleteReferral) {
      dispatch(enqueueNotification({ message: 'Referral removed' }))
      getDealReferrals({ dealId: dealID as string })
    }
  }, [isSuccessDeleteReferral])

  useEffect(() => {
    getDealReferrals({ dealId: dealID as string })
  }, [])

  return (
    <>
      <StyledGrid className={classes.root}>
        <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 118px)' }} className={classes.root}>
          <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHead} sortDirection={orderBy === 'Salesperson' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'Salesperson'}
                    direction={orderBy === 'Salesperson' ? order : 'asc'}
                    onClick={createSortHandler('Salesperson')}
                  >
                    Name
                    {orderBy === 'Salesperson' ? (
                      <Box component='span' sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHead} sortDirection={orderBy === 'Username' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'Username'}
                    direction={orderBy === 'Username' ? order : 'asc'}
                    onClick={createSortHandler('Username')}
                  >
                    Added By
                    {orderBy === 'Username' ? (
                      <Box component='span' sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHead} sortDirection={orderBy === 'UserCode' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'UserCode'}
                    direction={orderBy === 'UserCode' ? order : 'asc'}
                    onClick={createSortHandler('UserCode')}
                  >
                    Referral Fee
                    {orderBy === 'UserCode' ? (
                      <Box component='span' sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHead} sortDirection={orderBy === 'CommissionAmount' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'CommissionAmount'}
                    direction={orderBy === 'CommissionAmount' ? order : 'asc'}
                    onClick={createSortHandler('CommissionAmount')}
                  >
                    W9
                    {orderBy === 'CommissionAmount' ? (
                      <Box component='span' sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHead} sortDirection={orderBy === 'CommissionAmount' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'CommissionAmount'}
                    direction={orderBy === 'CommissionAmount' ? order : 'asc'}
                    onClick={createSortHandler('CommissionAmount')}
                  >
                    Note
                    {orderBy === 'CommissionAmount' ? (
                      <Box component='span' sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>

                <TableCell className={classes.tableHead} align={'right'}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(getComparator(order, orderBy), referralData as any[])?.map((item: any) => (
                <TableRow key={item.CommissionID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell
                    className={classes.tableCellName}
                    onClick={() => {
                      navigate(appRoute.DealsReferralAddEdit.replace(':dealID', dealID as string).replace(':referral', item?.ReferralID))
                    }}
                  >
                    {item.Name}
                  </TableCell>
                  <TableCell>{item?.AddedBy}</TableCell>
                  <TableCell>{item?.ReferralFee}</TableCell>
                  <TableCell>{item?.IsCompletedW9 ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{item?.Note}</TableCell>
                  <TableCell align={'right'}>
                    <Tooltip title='Edit'>
                      <IconButton
                        color='primary'
                        onClick={() => {
                          navigate(appRoute.DealsReferralAddEdit.replace(':dealID', dealID as string).replace(':referral', item?.ReferralID))
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete'>
                      <IconButton
                        color='error'
                        onClick={() => {
                          setDeleteConfirmDialog({ referralId: item?.ReferralID?.toString() as string })
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {referralData?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align='center'>
                    No results
                  </TableCell>
                </TableRow>
              )}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10} align='center'>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledGrid>
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Referral will be removed'}
      />
    </>
  )
}

export default DealsReferralTableBlock
